import { ALOHA_SCROLL_CONTAINER_ID, SIMPLE_SCROLL_CONTAINER_ID } from '@apps/guest/routes/GuestSite/GuestSite.constants';
import { withWindow } from '@shared/utils/withWindow';
import { throttle } from 'lodash-es';
import { useEffect, useState } from 'react';

/** Scroll offset past which `useScrollPastHero` reports the hero as scrolled past (MobileTopBarNav's bar appears). */
export const DEFAULT_SCROLL_PAST_HERO_THRESHOLD_PX = 100;

type UseScrollPastHeroOptions = Readonly<{
  threshold?: number;
}>;

/**
 * The element the guest site scrolls: the simple layout's scroll container, else the aloha
 * layout's, else the window itself (layouts that scroll the document). Read at call time, so call
 * it after the layout has mounted.
 */
export const resolveGuestScrollTarget = (global: Window): HTMLElement | Window =>
  global.document.getElementById(SIMPLE_SCROLL_CONTAINER_ID) || global.document.getElementById(ALOHA_SCROLL_CONTAINER_ID) || global;

export const useScrollPastHero = ({ threshold = DEFAULT_SCROLL_PAST_HERO_THRESHOLD_PX }: UseScrollPastHeroOptions = {}) => {
  const [isPastThreshold, setIsPastThreshold] = useState(false);

  useEffect(() => {
    return withWindow(global => {
      const target = resolveGuestScrollTarget(global);

      const handleScroll = throttle(() => {
        const scrollY = 'scrollY' in target ? target.scrollY : target.scrollTop;
        setIsPastThreshold(scrollY > threshold);
      }, 100);

      handleScroll();
      target.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        handleScroll.cancel();
        target.removeEventListener('scroll', handleScroll);
      };
    });
  }, [threshold]);

  return { isPastThreshold };
};
